import { useCallback, useEffect, useState } from "react";

/**
 * Cross-tab clipboard for ProgramBuilder exercises.
 *
 * Coaches routinely keep two programs open in two browser tabs and compare them
 * side by side; until now an exercise copied in one tab was invisible to the
 * other, because the clipboard was plain component state. Persisting it in
 * `localStorage` makes the copy survive reloads AND propagate to every other tab
 * through the native `storage` event.
 *
 * The payload holds raw program exercises. They are always re-cloned through
 * `cloneExerciseAsIndependent` on paste, so ids and `shared_*` links are
 * regenerated by the consumer — never trust the stored objects as-is.
 *
 * `localStorage` is scoped to the origin, not to the account: the payload
 * therefore carries the owner's user id and is ignored by anyone else, so a
 * clipboard left behind by a previous session cannot leak into another coach's
 * program on a shared browser.
 */

const CLIPBOARD_KEY = "kraveon:pb-clipboard";
const CLIPBOARD_VERSION = 1;
const MAX_ITEMS = 20;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const isBrowser = () => typeof window !== "undefined";

/**
 * Reads the clipboard, discarding anything malformed, stale or written by an
 * older payload version. Returns `null` when there is nothing usable.
 */
export function readProgramClipboard(userId) {
	if (!isBrowser()) return null;
	let raw;
	try {
		raw = localStorage.getItem(CLIPBOARD_KEY);
	} catch {
		return null;
	}
	if (!raw) return null;

	let parsed;
	try {
		parsed = JSON.parse(raw);
	} catch {
		clearProgramClipboard();
		return null;
	}

	if (!parsed || parsed.v !== CLIPBOARD_VERSION) {
		clearProgramClipboard();
		return null;
	}
	if (!Array.isArray(parsed.items) || parsed.items.length === 0) {
		clearProgramClipboard();
		return null;
	}
	// Written by a different account on this browser: not ours to paste.
	if (userId && parsed.u && parsed.u !== userId) return null;

	if (
		typeof parsed.ts !== "number" ||
		Date.now() - parsed.ts > MAX_AGE_MS ||
		Date.now() < parsed.ts - MAX_AGE_MS
	) {
		clearProgramClipboard();
		return null;
	}

	return {
		kind: parsed.kind === "day" ? "day" : "exercises",
		items: parsed.items.slice(0, MAX_ITEMS),
		source: parsed.source || {},
		ts: parsed.ts,
	};
}

/**
 * Builds and persists a clipboard payload.
 *
 * Returns `{ payload, persisted }`: `payload` is always usable in-memory, while
 * `persisted` reports whether it also reached `localStorage`. A failed write
 * (private mode, quota) degrades to the previous in-tab-only behaviour instead
 * of losing the copy.
 */
export function writeProgramClipboard({
	kind = "exercises",
	items,
	source,
	userId,
}) {
	const list = (Array.isArray(items) ? items : [items])
		.filter(Boolean)
		.slice(0, MAX_ITEMS);
	if (list.length === 0) return { payload: null, persisted: false };

	const payload = {
		v: CLIPBOARD_VERSION,
		kind: kind === "day" ? "day" : "exercises",
		items: list,
		source: source || {},
		u: userId || null,
		ts: Date.now(),
	};

	let persisted = false;
	try {
		localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
		persisted = true;
	} catch {
		// Private mode / quota exceeded: keep the in-memory copy only.
	}

	return { payload, persisted };
}

export function clearProgramClipboard() {
	if (!isBrowser()) return;
	try {
		localStorage.removeItem(CLIPBOARD_KEY);
	} catch {
		// Nothing to do: the in-memory state is reset by the caller anyway.
	}
}

/**
 * Clipboard state synced across tabs.
 *
 * The `storage` event only fires in the *other* tabs, so local mutations update
 * the state directly while remote ones arrive through the listener.
 */
export function useProgramClipboard(userId) {
	const [clipboard, setClipboard] = useState(() =>
		readProgramClipboard(userId),
	);

	useEffect(() => {
		setClipboard(readProgramClipboard(userId));
		const handleStorage = (event) => {
			// `event.key === null` means localStorage.clear() was called.
			if (event.key !== null && event.key !== CLIPBOARD_KEY) return;
			setClipboard(readProgramClipboard(userId));
		};
		window.addEventListener("storage", handleStorage);
		return () => window.removeEventListener("storage", handleStorage);
	}, [userId]);

	const copy = useCallback(
		(input) => {
			const { payload, persisted } = writeProgramClipboard({
				...input,
				userId,
			});
			setClipboard(payload);
			return { payload, persisted };
		},
		[userId],
	);

	const clear = useCallback(() => {
		clearProgramClipboard();
		setClipboard(null);
	}, []);

	return { clipboard, copy, clear };
}

// --- Drag & drop transfer ----------------------------------------------------
//
// Dragging an exercise out of the reference panel and onto a day of the editor
// uses the same payload shape as the clipboard. A dedicated MIME type is what
// lets the editor tell this apart from its own day/exercise reordering drags,
// which share the same drop targets.

export const EXERCISE_DRAG_MIME = "application/x-kraveon-pb-exercises";

export function setExerciseDragPayload(dataTransfer, items) {
	const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
	if (!dataTransfer || list.length === 0) return false;
	try {
		dataTransfer.setData(EXERCISE_DRAG_MIME, JSON.stringify(list));
		// Plain text fallback so dropping outside the app degrades gracefully.
		dataTransfer.setData(
			"text/plain",
			list.map((item) => item?.exercise_name || "").join(", "),
		);
		dataTransfer.effectAllowed = "copy";
		return true;
	} catch {
		return false;
	}
}

/**
 * During `dragover` the browser exposes the available types but not their
 * values, so this is the only check possible before the drop.
 */
export function hasExerciseDragPayload(dataTransfer) {
	if (!dataTransfer) return false;
	const types = dataTransfer.types;
	if (!types) return false;
	return Array.from(types).includes(EXERCISE_DRAG_MIME);
}

export function readExerciseDragPayload(dataTransfer) {
	if (!hasExerciseDragPayload(dataTransfer)) return null;
	try {
		const parsed = JSON.parse(dataTransfer.getData(EXERCISE_DRAG_MIME));
		if (!Array.isArray(parsed) || parsed.length === 0) return null;
		return parsed;
	} catch {
		return null;
	}
}
